/**
 * Exam Paper (authoring) constants.
 *
 * Mirrors the backend `ExamPaperItemType` enum and the exact AC character/count
 * limits enforced server-side (see `src/common/constants/exam-paper.constants.ts`
 * in the backend repo) so the builder canvas can validate client-side before
 * ever hitting the API.
 */

export enum ExamPaperItemType {
  MCQ = 'MCQ',
  LONG_ANSWER = 'LONG_ANSWER',
  SHORT_ANSWER = 'SHORT_ANSWER',
  MATH_QUESTION = 'MATH_QUESTION',
  DRAWING_QUESTION = 'DRAWING_QUESTION',
  TEXT_BLOCK = 'TEXT_BLOCK',
}

/**
 * Both label sets resolve at call time (`(t) => ...`), matching the
 * `EXAM_CATEGORY_OPTIONS` pattern in `exam.constant.ts`. Module-level resolution
 * would freeze the labels in whichever language the module was first imported
 * under, which is wrong for a live language switch.
 */
type TFn = (key: string, def?: string) => string;

/** The 5 fixed question types shown in "Add Question Field" (Text Block is added separately). */
export const QUESTION_FIELD_MENU_ITEMS = (
  t: TFn,
): Array<{
  itemType: ExamPaperItemType;
  label: string;
  icon: string;
}> => [
  {
    itemType: ExamPaperItemType.MCQ,
    label: t('exam_paper.type_mcq', 'Multiple Choice Question'),
    icon: 'pi pi-list',
  },
  {
    itemType: ExamPaperItemType.LONG_ANSWER,
    label: t('exam_paper.type_long_answer', 'Long Answer'),
    icon: 'pi pi-align-left',
  },
  {
    itemType: ExamPaperItemType.SHORT_ANSWER,
    label: t('exam_paper.type_short_answer', 'Short Answer'),
    icon: 'pi pi-minus',
  },
  {
    itemType: ExamPaperItemType.MATH_QUESTION,
    label: t('exam_paper.type_math', 'Math Question'),
    icon: 'pi pi-calculator',
  },
  {
    itemType: ExamPaperItemType.DRAWING_QUESTION,
    label: t('exam_paper.type_drawing', 'Drawing Question'),
    icon: 'pi pi-pencil',
  },
];

export const EXAM_PAPER_ITEM_TYPE_LABELS = (t: TFn): Record<ExamPaperItemType, string> => ({
  [ExamPaperItemType.MCQ]: t('exam_paper.type_mcq', 'Multiple Choice Question'),
  [ExamPaperItemType.LONG_ANSWER]: t('exam_paper.type_long_answer', 'Long Answer'),
  [ExamPaperItemType.SHORT_ANSWER]: t('exam_paper.type_short_answer', 'Short Answer'),
  [ExamPaperItemType.MATH_QUESTION]: t('exam_paper.type_math', 'Math Question'),
  [ExamPaperItemType.DRAWING_QUESTION]: t('exam_paper.type_drawing', 'Drawing Question'),
  [ExamPaperItemType.TEXT_BLOCK]: t('exam_paper.label_text_block', 'Text Block'),
});

// AC-mandated limits — keep numerically identical to the backend constants.
export const EXAM_PAPER_QUESTION_BODY_MAX = 2000;
export const EXAM_PAPER_TEXT_BLOCK_MAX = 2000;
export const EXAM_PAPER_LONG_DESC_MAX = 8000;
export const EXAM_PAPER_SHORT_MATH_DESC_MAX = 1000;
export const EXAM_PAPER_OPTION_MAX = 1000;
export const EXAM_PAPER_OPTIONS_MIN = 2;
export const EXAM_PAPER_OPTIONS_MAX = 8;
export const EXAM_PAPER_TITLE_MAX = 150;
/** Math Question attachment cap — raised from the shared 1-file limit to allow up to 5. */
export const EXAM_PAPER_MATH_MAX_FILES = 5;
/** Drawing Question stays capped at a single reference/model image. */
export const EXAM_PAPER_DRAWING_MAX_FILES = 1;
/** Text Block inline-image cap — client-side only (no server-side enforcement for this iteration). */
export const EXAM_PAPER_TEXT_BLOCK_MAX_FILES = 5;
/** Text Block inline-image size cap (bytes) — client-side only, matches Math/Drawing's unenforced-server-side precedent. */
export const EXAM_PAPER_TEXT_BLOCK_IMAGE_MAX_BYTES = 100 * 1024 * 1024;
/** Text Block inline-image allowed mime types — client-side only. */
export const EXAM_PAPER_TEXT_BLOCK_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];

/** Reusable-paper picker page size (bulk-fetch-once, client-side filter — same
 * precedent as the Fee Template select in AddEditEvent.tsx). */
export const EXAM_PAPER_REUSABLE_PAGE_SIZE = 100;
